import { isDarkTheme } from '..';
import { BuildClass, fsmData } from '../../../universal';
import { React, _ } from '../../lib';
import { setContextMenu } from './context-menu';
import { CJSX, useStateSync } from './meta-types';
import { stubDiv } from './stubs';
export const ResizeSplitter = React.forwardRef((props, ref) => {
    const elContainer = React.useRef(stubDiv);
    const cacheRect = React.useRef(getWidthRounded(stubDiv.getBoundingClientRect()));
    const [widths, setWidths] = React.useState(props.value ?? null);
    const widthsRef = React.useRef(widths);
    React.useEffect(() => {
        widthsRef.current = widths;
    }, [widths]);
    const panesRef = React.useRef(props.panes);
    React.useEffect(() => {
        panesRef.current = props.panes;
    }, [props.panes]);
    React.useEffect(() => {
        const runObserverEvent = () => {
            cacheRect.current = getWidthRounded(elContainer.current?.getBoundingClientRect() ?? cacheRect.current);
            updateWidthsForResize(widthsRef.current, cacheRect.current.width, panesRef.current);
        };
        const observer = new ResizeObserver(runObserverEvent);
        observer.observe(elContainer.current);
        runObserverEvent();
        return () => {
            observer.disconnect();
        };
    }, [elContainer.current]);
    useStateSync({
        propVal: props.value ?? null,
        setProp: v => {
            props.onUpdate?.(v);
        },
        stateVal: widths,
        setState: setWidths,
        compareFn: (p, s) => _.isEqual(p, s),
    });
    const [minMaxWidths, setMinMaxWidths] = React.useState(() => getMinMaxWidths(widths, cacheRect.current.width, props.panes) ?? []);
    React.useEffect(() => {
        const totalWidth = cacheRect.current.width;
        if (!widths) {
            return;
        }
        const newWidths = props.panes.map((pane, index) => {
            const minWidth = pane.minWidth ?? 0;
            const maxWidth = pane.maxWidth ?? totalWidth;
            let width = widths[index] ?? -1;
            if (!(pane.collapsible && width == 0) && width < minWidth) {
                width = Math.max(width, minWidth);
            }
            return Math.min(width, maxWidth);
        });
        if (!_.isEqual(newWidths, widths)) {
            updateWidths(newWidths);
        }
        const newMinMaxWidths = getMinMaxWidths(newWidths, cacheRect.current.width, props.panes);
        if (newMinMaxWidths && !_.isEqual(newMinMaxWidths, minMaxWidths)) {
            setMinMaxWidths(newMinMaxWidths);
        }
    }, [cacheRect.current.width, widths, props.panes, props.value]);
    const updateWidths = (newWidths, index, containerWidth) => {
        const newWidthsSanitised = calculateNewWidths(newWidths, containerWidth ?? cacheRect.current.width, props.panes, index ?? null, false);
        if (!_.isEqual(widths, newWidthsSanitised)) {
            setWidths(newWidthsSanitised);
        }
    };
    const updateWidthsForResize = (currentWidths, containerWidth, panes) => {
        const newWidthsSanitised = calculateNewWidths(currentWidths, containerWidth, panes, null, true);
        if (!_.isEqual(widthsRef.current, newWidthsSanitised)) {
            setWidths(newWidthsSanitised);
        }
    };
    React.useEffect(() => {
        updateWidths(widths);
    }, [
        cacheRect.current.width,
        elContainer.current,
        widths,
        props.panes,
        props.value,
    ]);
    const [dragging, setDragging] = React.useState(null);
    React.useImperativeHandle(ref, () => ({
        getElement: () => elContainer.current,
        reset: () => {
            updateWidths(null);
        },
    }));
    if (!minMaxWidths || !widths) {
        return (React.createElement("div", { ref: elContainer, className: BuildClass({
                'ui5-resize-splitter': true,
                [BuildClass(props.className)]: true,
                loading: true,
            }) }));
    }
    return (React.createElement("div", { ref: elContainer, className: BuildClass({
            'ui5-resize-splitter': true,
            [BuildClass(props.className)]: true,
        }) },
        React.createElement("div", { className: BuildClass({
                'resize-splitter-backdrop': true,
                active: dragging != null,
            }) }),
        props.panes.map((pane, index) => (React.createElement(React.Fragment, { key: index },
            React.createElement("div", { className: "ui5-resize-splitter-pane", style: {
                    width: `${widths[index]}px`,
                } }, pane.content),
            React.createElement(CJSX, { cond: index < props.panes.length - 1 },
                React.createElement(ResizeSplitterHandle, { borderColor: props.borderColor, pane: pane, isDragging: dragging == index, width: widths[index], minMaxWidth: minMaxWidths[index], isCollapsed: widths[index] == 0 && pane.collapsible, onDragStart: () => {
                        setDragging(index);
                    }, onDragEnd: newWidth => {
                        setDragging(null);
                        const newWidths = [...widths];
                        newWidths[index] = newWidth;
                        updateWidths(newWidths, index);
                    }, onUncollapse: () => {
                        const newWidths = [...widths].map(w => w ?? null);
                        newWidths[index] = pane.defaultWidth;
                        updateWidths(newWidths);
                    }, onResetWidths: () => {
                        updateWidths(null);
                    } })))))));
});
const ResizeSplitterHandle = (props) => (React.createElement("div", { className: "ui5-resize-splitter-divider-wrapper" },
    React.createElement("div", { className: "splitter-border", style: {
            backgroundColor: props.borderColor ??
                (isDarkTheme() ? 'hsl(0, 0%, 28%)' : '#aaaaaa'),
        } }),
    React.createElement("div", { className: BuildClass({
            'ui5-resize-splitter-collapsed-handle': true,
            hidden: !props.isCollapsed,
        }), role: "button", tabIndex: props.isCollapsed ? 0 : -1, "aria-label": "Restore collapsed pane", title: "Click to reset the collapsed pane to its default width", onClick: () => {
            props.onUncollapse();
        }, onKeyDown: e => {
            if (!props.isCollapsed) {
                return;
            }
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.onUncollapse();
            }
        } },
        React.createElement("img", { className: "arrow-img", alt: "Reset pane widths", src: "/static/img/svg/cb-arrow.svg" })),
    React.createElement("div", { className: BuildClass({
            'ui5-resize-splitter-divider': true,
            active: props.isDragging,
        }), role: "separator", "aria-orientation": "vertical", tabIndex: 0, "aria-label": "Resize panes", onMouseDown: e => {
            const element = e.currentTarget;
            const offset = e.clientX;
            props.onDragStart();
            document.body.style.userSelect = 'none';
            const minMax = props.minMaxWidth;
            const minOffset = minMax.minWidth - props.width;
            const maxOffset = minMax.maxWidth - props.width;
            const collapsible = props.pane.collapsible ?? false;
            const collapse_threshold = (minOffset - props.width) / 2;
            const mouseMoveHandler = (e1) => {
                let transX = e1.clientX - offset;
                if (collapsible && transX < collapse_threshold) {
                    transX = -props.width;
                }
                else if (transX < minOffset) {
                    transX = minOffset;
                }
                else if (transX > maxOffset) {
                    transX = maxOffset;
                }
                element.style.transform = `translate(${transX}px, 0px)`;
            };
            const mouseUpHandler = (e1) => {
                let transX = e1.clientX - offset;
                let collapsed = false;
                if (collapsible && transX < collapse_threshold) {
                    transX = -props.width;
                    collapsed = true;
                }
                else if (transX < minOffset) {
                    transX = minOffset;
                }
                else if (transX > maxOffset) {
                    transX = maxOffset;
                }
                element.style.transform = '';
                document.body.style.userSelect = '';
                document.removeEventListener('mousemove', mouseMoveHandler);
                document.removeEventListener('mouseup', mouseUpHandler);
                const newWidth = collapsed ? 0 : props.width + transX;
                props.onDragEnd(newWidth);
            };
            document.addEventListener('mousemove', mouseMoveHandler);
            document.addEventListener('mouseup', mouseUpHandler);
        }, onKeyDown: e => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.onResetWidths();
            }
        }, onContextMenu: e => {
            e.preventDefault();
            e.stopPropagation();
            setContextMenu({
                position: {
                    x: e.clientX,
                    y: e.clientY,
                },
                items: [
                    {
                        label: 'Reset Pane Widths',
                        onClick: () => {
                            props.onResetWidths();
                        },
                    },
                ],
            });
        } })));
const getMinMaxWidths = (widths, containerWidth, panes) => {
    if (!widths) {
        return null;
    }
    const ranges = panes.map((pane, index) => {
        const minWidth = pane.minWidth ?? 0;
        const maxWidth = pane.maxWidth ?? containerWidth;
        const width = widths[index] ?? -1;
        return {
            index: index,
            contract: width - minWidth,
            expand: maxWidth - width,
        };
    });
    const realMinMax = panes.map((_pane, index) => {
        const thisRange = ranges[index] ?? { index: -1, contract: -1, expand: -1 };
        const availableContract = _.sum(fsmData(ranges, {
            filter: x => x.index != index,
            map: x => x.expand,
        }));
        const availableExpand = _.sum(fsmData(ranges, {
            filter: x => x.index != index,
            map: x => x.contract,
        }));
        const width = widths[index] ?? -1;
        return {
            index: index,
            minWidth: width - Math.min(thisRange.contract, availableContract),
            maxWidth: width + Math.min(thisRange.expand, availableExpand),
        };
    });
    return realMinMax;
};
const calculateDefaultWidths = (containerWidth, panes) => {
    const nonFlexiWidth = _.sum(fsmData(panes, {
        filter: x => x.flexWidth == null,
        map: x => x.defaultWidth,
    }));
    const remainingWidth = Math.max(containerWidth - nonFlexiWidth, 0);
    const flexiSum = _.sum(fsmData(panes, {
        filter: x => x.flexWidth != null,
        map: x => x.flexWidth,
    }));
    const widthPerFlex = remainingWidth / flexiSum;
    return calculateNewWidths(panes.map(pane => {
        if (pane.flexWidth != null) {
            return Math.floor(widthPerFlex * pane.flexWidth);
        }
        return pane.defaultWidth;
    }), containerWidth, panes, null, false);
};
const applyFlexiDeficit = (input) => {
    const remainingFlexiPanes = input.panesWithIndex.filter(pane => pane.flexWidth != null && pane.index !== input.lastResizedIndex);
    const applyPassToPanes = (panes, state, totalFlexWidth, proportion) => {
        const pane = panes[0];
        if (!pane) {
            return state;
        }
        const width = input.widths?.[pane.index] ?? 0;
        const thisPaneProportion = (pane.flexWidth ?? 0) / totalFlexWidth;
        const diff = state.deficitRemaining * proportion * thisPaneProportion;
        const newWidth = Math.round(width + diff);
        const nextWidths = [...state.newWidths];
        nextWidths[pane.index] = newWidth;
        const nextState = {
            deficitRemaining: state.deficitRemaining - (newWidth - width),
            newWidths: nextWidths,
        };
        return applyPassToPanes(panes.slice(1), nextState, totalFlexWidth, proportion);
    };
    const runFlexiPasses = (panes, state) => {
        if (state.deficitRemaining === 0 || panes.length === 0 || state.passes >= 20) {
            return {
                deficitRemaining: state.deficitRemaining,
                newWidths: state.newWidths,
            };
        }
        const totalFlexWidth = _.sum(panes.map(x => x.flexWidth ?? 0));
        const proportions = panes.map(pane => {
            const width = input.widths?.[pane.index] ?? 0;
            const thisPaneProportion = (pane.flexWidth ?? 0) / totalFlexWidth;
            const fullDiff = state.deficitRemaining * thisPaneProportion;
            const minWidth = pane.minWidth ?? 0;
            const maxWidth = pane.maxWidth ?? input.containerWidth;
            if (state.deficitRemaining > 0) {
                return Math.min((maxWidth - width) / fullDiff, 1);
            }
            if (state.deficitRemaining < 0) {
                return Math.min((minWidth - width) / fullDiff, 1);
            }
            return 1;
        });
        const proportion = _.min(proportions) ?? 0;
        const updated = applyPassToPanes(panes, { deficitRemaining: state.deficitRemaining, newWidths: state.newWidths }, totalFlexWidth, proportion);
        const nextPanes = panes.filter((__, index) => {
            const p = proportions[index] ?? 0;
            return Math.abs(p - proportion) > EPSILON;
        });
        return runFlexiPasses(nextPanes, {
            deficitRemaining: updated.deficitRemaining,
            newWidths: updated.newWidths,
            passes: state.passes + 1,
        });
    };
    return runFlexiPasses(remainingFlexiPanes, {
        deficitRemaining: input.deficitRemaining,
        newWidths: input.newWidths,
        passes: 0,
    });
};
const applyNonFlexiDeficit = (input) => {
    const remainingNonFlexiPanes = input.panesWithIndex.filter(pane => pane.flexWidth == null);
    const applyPassToPanes = (panes, state, proportion, paneCount) => {
        const pane = panes[0];
        if (!pane) {
            return state;
        }
        const width = input.widths?.[pane.index] ?? 0;
        const thisPaneProportion = 1 / paneCount;
        const diff = state.deficitRemaining * proportion * thisPaneProportion;
        const newWidth = Math.floor(width + diff);
        const nextWidths = [...state.newWidths];
        nextWidths[pane.index] = newWidth;
        const nextState = {
            deficitRemaining: state.deficitRemaining - (newWidth - width),
            newWidths: nextWidths,
        };
        return applyPassToPanes(panes.slice(1), nextState, proportion, paneCount);
    };
    const runNonFlexiPasses = (panes, state) => {
        if (state.deficitRemaining === 0 || panes.length === 0 || state.passes >= 20) {
            return {
                deficitRemaining: state.deficitRemaining,
                newWidths: state.newWidths,
            };
        }
        const proportions = panes.map(pane => {
            const width = input.widths?.[pane.index] ?? 0;
            const thisPaneProportion = 1 / panes.length;
            const fullDiff = state.deficitRemaining * thisPaneProportion;
            const minWidth = pane.minWidth ?? 0;
            const maxWidth = pane.maxWidth ?? input.containerWidth;
            if (state.deficitRemaining > 0) {
                return Math.min((maxWidth - width) / fullDiff, 1);
            }
            if (state.deficitRemaining < 0) {
                return Math.min((minWidth - width) / fullDiff, 1);
            }
            return 1;
        });
        const proportion = _.min(proportions) ?? 0;
        const updated = applyPassToPanes(panes, {
            deficitRemaining: state.deficitRemaining,
            newWidths: state.newWidths,
        }, proportion, panes.length);
        const nextPanes = panes.filter((__, index) => {
            const p = proportions[index] ?? 0;
            return Math.abs(p - proportion) > EPSILON;
        });
        return runNonFlexiPasses(nextPanes, {
            deficitRemaining: updated.deficitRemaining,
            newWidths: updated.newWidths,
            passes: state.passes + 1,
        });
    };
    return runNonFlexiPasses(remainingNonFlexiPanes, {
        deficitRemaining: input.deficitRemaining,
        newWidths: input.newWidths,
        passes: 0,
    });
};
const calculateNewWidths = (widths, containerWidth, panes, lastResizedIndex, isContainerResize, recursiveCounter = 0) => {
    if (!widths && containerWidth == 0) {
        return null;
    }
    widths ??= calculateDefaultWidths(containerWidth, panes);
    if (!widths) {
        return null;
    }
    const currentTotalWidth = _.sum(widths.map(x => x ?? 0));
    const deficit = containerWidth - currentTotalWidth;
    if (containerWidth == 0) {
        return widths;
    }
    const panesWithIndex = _.map(panes, (x, i) => ({ ...x, index: i }));
    let deficitRemaining = deficit;
    let newWidths = widths.map(x => x ?? 0);
    const flexResult = applyFlexiDeficit({
        panesWithIndex,
        widths,
        newWidths,
        deficitRemaining,
        containerWidth,
        lastResizedIndex,
    });
    deficitRemaining = flexResult.deficitRemaining;
    newWidths = flexResult.newWidths;
    if (!isContainerResize) {
        const nonFlexResult = applyNonFlexiDeficit({
            panesWithIndex,
            widths,
            newWidths,
            deficitRemaining,
            containerWidth,
        });
        deficitRemaining = nonFlexResult.deficitRemaining;
        newWidths = nonFlexResult.newWidths;
    }
    const lastResizedPane = panes[lastResizedIndex ?? -1];
    if (deficitRemaining && lastResizedIndex != null && lastResizedPane?.flexWidth) {
        const width = widths[lastResizedIndex] ?? -1;
        const minWidth = lastResizedPane.minWidth ?? 0;
        const maxWidth = lastResizedPane.maxWidth ?? containerWidth;
        const newWidth = Math.min(Math.max(width + deficitRemaining, minWidth), maxWidth);
        newWidths[lastResizedIndex] = newWidth;
        deficitRemaining -= newWidth - width;
    }
    if (Math.abs(deficitRemaining) < EPSILON) {
        return newWidths;
    }
    if (recursiveCounter < 5) {
        return calculateNewWidths(newWidths, containerWidth, panes, lastResizedIndex, isContainerResize, recursiveCounter + 1);
    }
    console.warn(`ResizeSplitter deficit: ${deficitRemaining}`);
    return newWidths;
};
const getWidthRounded = (rect) => ({
    width: Math.floor(rect.width),
});
const EPSILON = 1e-9;
//# sourceMappingURL=resize-split.js.map